// Libraries
import PropTypes from 'prop-types';
import React from 'react';

// App
import {colors} from '@supermove/styles';

// Components
import Styled from '../Styled';

const Container = Styled.View<{color: string}>`
  flex-direction: row;
  align-items: center;
  min-height: 60px;
  padding-vertical: 10px;
  padding-horizontal: 20px;
  border-radius: 5px;
  background-color: ${(props) => colors.alpha(props.color, 0.1)}
`;

const Text = Styled.H6<{color: string}>`
  flex: 1;
  color: ${(props) => props.color};
`;

interface NotificationProps {
  color: string;
  text: string | React.ReactNode;
  style?: object;
  children?: React.ReactNode;
}

/**
 * Creates a new Notification component each time. This way they can all have
 * different defaultProps.
 */
const createNotification = ({color}: {color?: string}) => {
  const Notification = ({color, text, style, children}: NotificationProps) => (
    <Container color={color} style={style}>
      {React.isValidElement(text) ? (
        React.cloneElement<{color: string}>(text as React.ReactElement<{color: string}>, {
          color,
        })
      ) : (
        <Text color={color}>{text}</Text>
      )}
      {children}
    </Container>
  );

  Notification.propTypes = {
    color: PropTypes.string,
  };

  Notification.defaultProps = {
    color,
  };

  return Notification;
};

type NotificationType = {
  (props: NotificationProps): JSX.Element;
  Text: typeof Text;
  Success: typeof Notification;
  Info: typeof Notification;
  Default: typeof Notification;
  Warning: typeof Notification;
  Error: typeof Notification;
};

// Create the base Notification.
// @ts-expect-error assigning to const one at a time doesn't play well with TS
const Notification: NotificationType = createNotification({});

// Additional Components
Notification.Text = Text;

// Types
// @ts-expect-error assigning to const one at a time doesn't play well with TS
Notification.Success = createNotification({color: colors.green.status});
// @ts-expect-error assigning to const one at a time doesn't play well with TS
Notification.Info = createNotification({color: colors.blue.accentDark});
// @ts-expect-error assigning to const one at a time doesn't play well with TS
Notification.Default = createNotification({color: colors.yellow.hover});
// @ts-expect-error assigning to const one at a time doesn't play well with TS
Notification.Warning = createNotification({color: colors.orange.status});
// @ts-expect-error assigning to const one at a time doesn't play well with TS
Notification.Error = createNotification({color: colors.red.warning});

export default Notification;
